import {Box, Group, Stack, Text} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {format, parseISO} from 'date-fns'
import {DataTable, type DataTableColumn} from 'mantine-datatable'

import {commitmentBandColor} from 'src/theme/semantic-colors'

import type {JobCommitment} from './schemas'

type StockJobCommitmentsTableProps = {
    commitments: JobCommitment[]
}

function formatDates(commitment: JobCommitment) {
    const start = format(parseISO(commitment.startDate), 'MMM d')
    return commitment.endDate == null
        ? `${start} – no end date`
        : `${start} – ${format(parseISO(commitment.endDate), 'MMM d')}`
}

/** Only units on site have a location; the rest have not been deployed anywhere. */
function LocationCell({commitment}: {commitment: JobCommitment}) {
    if (commitment.locationLabels.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                — not deployed
            </Text>
        )
    }
    if (commitment.locationLabels.length === 1) {
        return <Text size="sm">{commitment.locationLabels[0]}</Text>
    }
    return <Text size="sm">{commitment.locationLabels.length} locations</Text>
}

function CountCell({count, band}: {count: number; band?: 'on_site'}) {
    if (count === 0) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        )
    }
    if (band === undefined) {
        return <Text size="sm">{count}</Text>
    }
    return (
        <Group gap={6} wrap="nowrap" justify="flex-end">
            <Box
                bg={commitmentBandColor(band)}
                style={{width: 8, height: 8, borderRadius: 2, flexShrink: 0}}
            />
            <Text size="sm">{count}</Text>
        </Group>
    )
}

export function StockJobCommitmentsTable({commitments}: StockJobCommitmentsTableProps) {
    if (commitments.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No jobs are holding these mats.
            </Text>
        )
    }

    const committed = commitments.reduce(
        (sum, commitment) => sum + commitment.onSiteCount + commitment.awaitingDeploymentCount,
        0,
    )
    const returned = commitments.reduce((sum, commitment) => sum + commitment.returnedCount, 0)
    const columns: DataTableColumn<JobCommitment>[] = [
        {
            accessor: 'jobName',
            title: 'Job',
            render: (commitment) => (
                <Link to="/jobs/$jobId" params={{jobId: commitment.jobId}}>
                    {commitment.jobName}
                </Link>
            ),
        },
        {accessor: 'projectName', title: 'Order'},
        {
            accessor: 'locationLabels',
            title: 'Location',
            render: (commitment) => <LocationCell commitment={commitment} />,
        },
        {accessor: 'startDate', title: 'Dates', render: formatDates},
        {
            accessor: 'assignedCount',
            title: 'Assigned',
            textAlign: 'right',
            render: (commitment) => <CountCell count={commitment.assignedCount} />,
        },
        {
            accessor: 'onSiteCount',
            title: 'On site',
            textAlign: 'right',
            render: (commitment) => <CountCell count={commitment.onSiteCount} band="on_site" />,
        },
        {
            accessor: 'awaitingDeploymentCount',
            title: 'Awaiting deployment',
            textAlign: 'right',
            render: (commitment) => <CountCell count={commitment.awaitingDeploymentCount} />,
        },
        {
            accessor: 'returnedCount',
            title: 'Returned',
            textAlign: 'right',
            render: (commitment) => <CountCell count={commitment.returnedCount} />,
        },
    ]

    return (
        <Stack gap="xs">
            <DataTable
                withTableBorder
                withColumnBorders
                striped
                textSelectionDisabled
                idAccessor="jobId"
                records={commitments}
                columns={columns}
            />
            <Text ta="right" size="sm" c="dimmed">
                {commitments.length === 1 ? '1 job' : `${commitments.length} jobs`} ·{' '}
                <Text span fw={600} c="var(--mantine-color-text)">
                    {committed}
                </Text>{' '}
                units still committed
                {returned > 0 && ` · ${returned} returned and back in the pool`}
            </Text>
        </Stack>
    )
}
